import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { CircularTimer } from './CasinoTimer'
import BetTableSwitch from './BetTableSwitch'
import CasinoResultModal from './CasinoResultModal'
import CasinoMyBetsSidebar from './CasinoMyBetsSidebar'
import CasinoRulesModal from './CasinoRulesModal'
import CasinoRulesSwitch from './rules/CasinoRulesSwitch'
import useCasinoGame from '../../hooks/casino/useCasinoGame'
import useCasinoLastResults from '../../hooks/casino/useCasinoLastResults'
import useCasinoMyBets from '../../hooks/casino/useCasinoMyBets'
import useCasinoStream from '../../hooks/casino/useCasinoStream'
import { getGameConfig } from '../../config/casinoGames'
import { computeAdminBook, getMarketsForGame } from '../../utils/casinoBook'
import { formatGoalResultText } from '../../utils/casinoUtils'
const REMARK_ICON = 'https://wver.sprintstaticdata.com/v230/static/front/img/icons/remark.png'
const GOAL_SOCCER_BALL_IMG = 'https://wver.sprintstaticdata.com/v230/static/admin/img/balls/soccer-ball.png'

// Full live-game screen for one gtype — admin reference DOM: game-header +
// video (with circular timer + right icons) + market-container bet table +
// last-results strip, and the My Bets right sidebar. Logic stays in the
// hooks; admin is read-only so bet clicks are no-ops.
export default function CasinoGameScreen({ gtype }) {
  const GAME_TYPE = gtype
  const GAME_CONFIG = getGameConfig(GAME_TYPE) || { name: GAME_TYPE, tableClass: GAME_TYPE, dataType: 'object' }
  const isArray = GAME_CONFIG.dataType === 'array'

  const isAuth = useSelector((s) => Boolean(s.auth.token))
  const [resultMid, setResultMid] = useState(null)
  const [showRules, setShowRules] = useState(false)
  const [showLr, setShowLr] = useState(true)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const { data: gameData } = useCasinoGame(GAME_TYPE, { isArray })
  const { data: lastResults = [], refetch: refetchLastResults } = useCasinoLastResults(GAME_TYPE)
  const { streamUrl } = useCasinoStream(GAME_TYPE)

  const gameObj = isArray ? (Array.isArray(gameData) ? gameData[0] : null) : gameData
  const mid = gameObj?.mid ?? ''
  const lt = gameObj?.lt ?? 0
  const ft = gameObj?.ft ?? 30
  const rem = gameObj?.remark || gameObj?.rem || ''
  const goalResultText = (GAME_TYPE === 'goal' || GAME_TYPE === 'goal2') && gameObj?.rdesc
    ? formatGoalResultText(gameObj.rdesc)
    : ''

  const { myBets } = useCasinoMyBets(GAME_TYPE, isAuth)

  // Open downline bets se admin book calculate (koi exposure API nahi)
  const exposures = useMemo(
    () => computeAdminBook(myBets, getMarketsForGame(GAME_TYPE, gameObj)),
    [myBets, GAME_TYPE, gameObj]
  )

  // New round started → refresh the last-results strip
  useEffect(() => {
    if (mid) refetchLastResults()
  }, [mid, refetchLastResults])

  const handleBet = useCallback(() => {}, [])

  const renderResult = useCallback((r, i) => (
    <span key={i} className="resultb" onClick={() => setResultMid(r.mid || null)}>
      {'\n        R\n      '}
    </span>
  ), [])

  return (
    <>
      <div className="center-main-container">
        <div className="center-content">
          <div className="casino-container">
            <div className={`casino-table ${GAME_CONFIG.tableClass}`}>

              <div className="game-header">
                <span className="game-header-name">{GAME_CONFIG.name}</span>
                {' '}
                <span className="float-right game-header-date">Round ID: {mid}</span>
              </div>
              {' '}
              <div className="container-fluid container-fluid-5">
                <div className="row row5">
                  <div className="col-xl-12">
                    <div className="casino-video mt-0">
                      <div className="video-box-container">
                        <div className="video-box">
                          {streamUrl && <iframe src={streamUrl}></iframe>}
                          {goalResultText && (
                            <div className="cricket20ballpopup">
                              <img src={GOAL_SOCCER_BALL_IMG} alt="" />
                              <span>{goalResultText}</span>
                            </div>
                          )}
                          {' '}
                        </div>
                      </div>
                      {' '}
                      <CircularTimer timeLeft={lt} fullTime={ft} />
                      {' '}
                      <div className="casino-video-right-icons">
                        <div title="Home" className="casino-video-home-icon">
                          <Link to="/admin/casino/list" className="" style={{ color: 'var(--text-highlight)' }}>
                            <i className="fas fa-home"></i>
                          </Link>
                        </div>
                        {' '}
                        <div title="Rules" className="casino-video-rules-icon" onClick={() => setShowRules((v) => !v)}>
                          <i className="fas fa-info-circle"></i>
                        </div>
                        {' '}
                        <div title="Last Results" className="casino-video-lr-icon" onClick={() => setShowLr((v) => !v)}>
                          <i className={`fas ${showLr ? 'fa-chevron-circle-up' : 'fa-chevron-circle-down'}`}></i>
                        </div>
                      </div>
                      {' '}
                      <div>{showRules && (
                        <div
                          id="casino-vieo-rules"
                          aria-modal="true"
                          className="casino-vieo-rules show d-none-small show-rules"
                          style={{ display: 'block' }}
                        >
                          <div className="rules-header">
                            <div>Rules</div>
                            {' '}
                            <i className="fas fa-times" onClick={() => setShowRules(false)}></i>
                          </div>
                          {' '}
                          <div className="rules-body">
                            <CasinoRulesSwitch gameType={GAME_TYPE} />
                          </div>
                        </div>
                      )}</div>
                      {' '}
                      {!isMobile && (
                        <div className={`casino-video-last-results${showLr ? '' : ' hide-lr'}`}>
                          {lastResults.map(renderResult)}
                          {' '}
                          <Link to={`/admin/reports/casinoresult/${GAME_TYPE}`} className="result-more">
                            {'\n    ...\n  '}
                          </Link>
                          {' '}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {' '}
              <div className="market-container">
                <BetTableSwitch
                  gameType={GAME_TYPE}
                  gameData={gameData}
                  onBet={handleBet}
                  exposures={exposures}
                />
              </div>
              {' '}
              {rem && (
                <div className="casino-remark mt-1">
                  <div className="remark-icon">
                    <img src={REMARK_ICON} />
                  </div>
                  <marquee>{rem}</marquee>
                </div>
              )}
              {' '}
              {isMobile && (
                <div className="casino-video-last-results">
                  {lastResults.map(renderResult)}
                  {' '}
                  <Link to={`/admin/reports/casinoresult/${GAME_TYPE}`} className="result-more">
                    {'\n    ...\n  '}
                  </Link>
                  {' '}
                </div>
              )}

            </div>
          </div>
        </div>
        {' '}
        <CasinoMyBetsSidebar myBets={myBets} gameName={GAME_TYPE} outerSimpleBar={GAME_TYPE === 'goal2'} />
      </div>

      <CasinoRulesModal
        show={showRules}
        onClose={() => setShowRules(false)}
        tableClass=""
        gameType={GAME_TYPE}
      >
        <CasinoRulesSwitch gameType={GAME_TYPE} />
      </CasinoRulesModal>

      <CasinoResultModal
        show={!!resultMid}
        onClose={() => setResultMid(null)}
        gameType={GAME_TYPE}
        gameName={GAME_CONFIG.name}
        mid={resultMid}
      />
    </>
  )
}
